import type {
  ReactElement} from 'react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { useDropzone } from 'react-dropzone';

import {
  FormHelperText,
  FormControl,
  FormLabel,
  Chip,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import message from 'plugins/utils/message';

type FileObject = { file: File; data: string | ArrayBuffer | null };

type FormDialogRefProps = {
  isPrepared: () => boolean;
};

type FileItem = {
  id: string;
  fileName: string;
  filePath: string;
  fileType: string; // All types: https://www.freeformatter.com/mime-types-list.html
  fileObject?: FileObject;
};

type OnChangeFunction = (
  value:
    | string
    | number
    | boolean
    | null
    | (string | number | boolean | null)[],
  formik?: unknown,
  fieldValue?: unknown,
) => void | Promise<void>;

const DEFAULT_FILES_LIMIT = 15;
const DEFAULT_ACCEPTED_FILES: string[] = []; // More info: https://react-dropzone.js.org/#section-accepting-specific-file-types Ej: ['image/*', 'video/*', 'application/*']
const DEFAULT_MAX_FILE_SIZE = 150 * 1024 * 1024; // bytes
const DEFAULT_DROPZONE_TEXT = `Arrastre y suelte un archivo aquí o haga click. (Tamaño máximo: 150MB)`;

type ProgressState = { id: string; value: number } | null;

type Props = {
  name: string;
  label: string;
  value?: string;
  disabled?: boolean;
  fieldRequired?: string;
  error?: boolean;
  helperText?: string;
  formik?: unknown;
  filesLimit?: number;
  filesExt?: string[];
  acceptedFiles?: string[];
  maxFileSize?: number;
  dropzoneText?: string;
  variant?: 'filled' | 'standard' | 'outlined';
  onChange?: OnChangeFunction;
};

const stripedBg = `linear-gradient(-45deg, rgba(255,255,255,.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.2) 50%, rgba(255,255,255,.2) 75%, transparent 75%, transparent)`;

const DropzoneComponent = (
  props: Props,
  ref: React.Ref<FormDialogRefProps>,
): ReactElement => {
  const { label, error, helperText } = props;

  const errorText = '';

  const _val = typeof props.value !== 'undefined' ? props.value : '[]';
  let VALUE: FileItem[] = [];
  try {
    VALUE = _val ? (JSON.parse(_val || '[]') as FileItem[]) : [];
  } catch (err) {
    console.log(err);
  }
  const FILES_EXT = props.filesExt;
  const FILES_LIMIT = props.filesLimit || DEFAULT_FILES_LIMIT;
  const MAX_FILE_SIZE = props.maxFileSize || DEFAULT_MAX_FILE_SIZE;
  const ACCEPTED_FILES = props.acceptedFiles || DEFAULT_ACCEPTED_FILES;
  const DROPZONE_TEXT = props.dropzoneText || DEFAULT_DROPZONE_TEXT;

  const FIELD_REQUIRED = props.fieldRequired;
  const [DISABLED, setDisabled] = useState(false);
  const requiredValue: unknown = FIELD_REQUIRED ? undefined : undefined;
  useEffect(() => {
    const disableByProp =
      typeof props.disabled !== 'undefined' ? props.disabled : false;
    let newFieldDisabled = disableByProp;
    if (!disableByProp && FIELD_REQUIRED) {
      newFieldDisabled =
        requiredValue === undefined ||
        requiredValue === '' ||
        requiredValue === null ||
        requiredValue === '[]' || // Para los archivos adjuntos (MyDropzone)
        requiredValue === '{}' || // Para los grupos de checkbox (MyCheckboxGroup)
        (Array.isArray(requiredValue) && requiredValue.length === 0);
    }
    setDisabled(newFieldDisabled);
  }, [props.disabled, FIELD_REQUIRED, requiredValue]);

  const progressList = [
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
    useState<ProgressState>(null),
  ];

  const getExt = (obj: FileObject): string => {
    const dotIndex = obj.file.name.indexOf('.');
    if (dotIndex > -1) {
      return obj.file.name.split('.').pop() || '';
    }
    return '';
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isUploading = (fileItem?: FileItem): boolean => {
    if (!fileItem) return progressList.some((state) => state[0] !== null);
    return progressList.some(
      (state) => state[0] !== null && state[0].id === fileItem.id,
    );
  };

  const handleDropzoneOnAdd = (newFileObjs: FileObject[]) => {
    if (newFileObjs.length > FILES_LIMIT) {
      message.error('Solo puede cargar ' + FILES_LIMIT + ' archivo(s).');
      return;
    }
    if (VALUE.length + newFileObjs.length > FILES_LIMIT) {
      message.error('Solo puede cargar ' + FILES_LIMIT + ' archivo(s).');
      return;
    }

    if (FILES_EXT) {
      const EXT_VALIDAS = FILES_EXT.map((ext) => `*.${ext}`).join(', ');
      const archivosNoValidos = newFileObjs.filter((obj) => {
        const ext = getExt(obj);
        const esValido = FILES_EXT.includes(ext);
        if (!esValido) {
          message.error(
            `Formato de archivo incorrecto (${obj.file.name}). Extensiones válidas: ${EXT_VALIDAS}`,
          );
        }
        return !esValido;
      });
      if (archivosNoValidos.length > 0) return;
    }

    const newFileItems: FileItem[] = newFileObjs.map((fileObject) => ({
      id: uuid(),
      fileName: fileObject.file.name,
      filePath: '',
      fileType: '*',
      fileObject: fileObject,
    }));

    const updatedFiles = [...VALUE, ...newFileItems];

    if (props.onChange) void props.onChange(JSON.stringify(updatedFiles));
  };

  const handleDropzoneOnDelete = async (deleteFileItem: FileItem) => {
    if (isUploading(deleteFileItem)) {
      message.warning('Por favor espere a que se guarde el archivo.');
      return;
    }
    const newFileItemValue = VALUE.filter(
      (item: FileItem) => item.id !== deleteFileItem.id,
    );
    const newValue = JSON.stringify(
      newFileItemValue.map(({ id, fileName, filePath, fileType }) => ({
        id,
        fileName,
        filePath,
        fileType,
      })),
    );

    if (props.onChange) void props.onChange(newValue);
  };

  const handleClick = (fileItem: FileItem) => {
    if (isUploading(fileItem)) {
      message.warning('Por favor espere a que se guarde el archivo.');
      return;
    }

    const fileData = fileItem.fileObject?.data;
    const base64Data = (typeof fileData === 'string' ? fileData : '').split(',')[1];

    if (!base64Data) {
      message.error('No se encontró el archivo.');
      return;
    }

    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64Data)) {
      message.error('La cadena base64 no es válida.');
      return;
    }

    // Convertir base64 a un Blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const fileType =
      fileItem.fileObject?.file?.type || getFileTypeFromName(fileItem.fileName);

    const blob = new Blob([byteArray], { type: fileType });
    const FILE_URL = URL.createObjectURL(blob);

    window.open(FILE_URL);
  };

  const getFileTypeFromName = (fileName: string) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();

    if (!extension) return 'application/octet-stream';

    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      txt: 'text/plain',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    return mimeTypes[extension] ?? 'application/octet-stream';
  };

  const handleOnAlert = (msg: string, variant: string) => {
    if (variant === 'error') {
      if (msg.includes('File is too big')) {
        const maxSize = Number(MAX_FILE_SIZE / 1024 / 1024);
        return message.error(
          `El archivo es demasiado grande. Tamaño máximo permitido: ${maxSize}MB`,
        );
      }
      console.log(`Error al adjuntar el archivo. ${msg}`);
    }
  };

  const truncateText = (text: string, max: number = 20) => {
    if (text.length <= max) return text;
    const middle = parseInt(`${max / 2}`);
    const a = text.substr(0, middle);
    const b = text.substr(text.length - middle);
    return `${a} ... ${b}`;
  };

  const readFileAsDataURL = (
    file: File,
  ): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result || null);
      reader.readAsDataURL(file);
    });

  const buildAccept = (mimeTypes: string[]) => {
    if (!mimeTypes.length) return undefined;
    return mimeTypes.reduce(
      (acc, mime) => ({ ...acc, [mime]: [] }),
      {} as Record<string, string[]>,
    );
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, rejections) => {
      rejections.forEach((r) => {
        r.errors.forEach((e) => {
          if (e.code === 'file-too-large')
            handleOnAlert('File is too big', 'error');
        });
      });
      if (!acceptedFiles.length) return;
      void (async () => {
        const fileObjects: FileObject[] = await Promise.all(
          acceptedFiles.map(async (file) => ({
            file,
            data: await readFileAsDataURL(file),
          })),
        );
        handleDropzoneOnAdd(fileObjects);
      })();
    },
    maxSize: MAX_FILE_SIZE,
    accept: buildAccept(ACCEPTED_FILES),
    disabled: DISABLED,
    multiple: true,
  });

  const refHandler = () => ({
    isPrepared: () => !isUploading(),
  });
  useImperativeHandle(ref, refHandler, [isUploading]);

  return (
    <FormControl sx={{ width: '100%', pt: 0 }} error={error || !!errorText}>
      <Box sx={{ borderTopLeftRadius: '4px', borderTopRightRadius: '4px', background: '#e8e8e8' }}>
        <FormLabel component="legend" sx={{ p: '8px 8px 8px 12px' }}>
          {label}
        </FormLabel>
        {isUploading() && (
          <Box
            sx={{
              border: 'dashed',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              color: 'rgba(34,47,62,.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '80px',
              p: 1,
              backgroundColor: '#a3cc72',
              backgroundImage: stripedBg,
              backgroundSize: '50px 50px',
              '@keyframes formDropzoneMove': { '0%': { backgroundPosition: '0 0' }, '100%': { backgroundPosition: '50px 50px' } },
              animation: 'formDropzoneMove 2s linear infinite',
            }}
          >
            <Typography align="center" variant="h6">
              Guardando archivos...
            </Typography>
            <Typography align="center" variant="body1">
              Por favor espere a que se guarden todos los archivos antes de
              continuar.
            </Typography>
          </Box>
        )}
        <Box
          {...getRootProps()}
          sx={{
            display: isUploading() ? 'none' : 'flex',
            minHeight: '80px',
            p: 1,
            m: '0 8px 8px 8px',
            border: '2px dashed rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            textAlign: 'center',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <input {...getInputProps()} />
          <Box
            component="p"
            sx={{
              color: errorText ? 'error.main' : 'rgba(34,47,62,.7)',
              fontSize: '18px',
              p: 0,
              m: '8px 0 0 0',
            }}
          >
            {DROPZONE_TEXT}
          </Box>
        </Box>
        <Box sx={{ minHeight: '8px' }}>
          {VALUE.map((item) => {
            const state = progressList.find(
              (state) => state[0] !== null && state[0].id === item.id,
            );
            const value = state && state[0] !== null ? state[0].value : null;
            const fileNameOriginal = item.fileName.replace(`${item.id}-`, '');
            return (
              <Box key={item.id} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', p: '5px' }}>
                <Tooltip title={fileNameOriginal} placement="top">
                  <Chip
                    sx={{ zIndex: 2, ...(value === null && { backgroundColor: '#ffffff' }) }}
                    label={truncateText(fileNameOriginal)}
                    variant="outlined"
                    deleteIcon={
                      value !== null ? (
                        <CircularProgress size="small" />
                      ) : (
                        <CloseIcon />
                      )
                    }
                    onDelete={() => handleDropzoneOnDelete(item)}
                    onClick={() => handleClick(item)}
                    disabled={DISABLED}
                  />
                </Tooltip>
                {value !== null ? (
                  <LinearProgress
                    sx={{
                      position: 'absolute',
                      width: 'calc(100% - 10px)',
                      height: 'calc(100% - 10px)',
                      bottom: '4px',
                      left: '5px',
                      borderRadius: '16px',
                      backgroundColor: '#ffffff',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#a3cc72',
                        backgroundImage: stripedBg,
                        backgroundSize: '50px 50px',
                        '@keyframes formDropzoneMove': { '0%': { backgroundPosition: '0 0' }, '100%': { backgroundPosition: '50px 50px' } },
                        animation: 'formDropzoneMove 2s linear infinite',
                      },
                    }}
                    variant="determinate"
                    value={value}
                  />
                ) : (
                  <></>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <FormHelperText sx={{ pl: 1.5 }}>
        {helperText || errorText}
      </FormHelperText>
    </FormControl>
  );
};

export const FormInputDropzone = forwardRef(DropzoneComponent);
